<script lang="ts">
	import type { Snippet } from 'svelte';
	import { Layers, Moon, Sun } from 'lucide-svelte';
	import type { ThemePreference } from '$lib/theme';

	interface Props {
		title: string;
		subtitle?: string;
		legend?: Snippet;
		theme?: ThemePreference;
		onThemeToggle?: () => void;
	}

	let { title, subtitle, legend, theme = 'dark', onThemeToggle }: Props = $props();
</script>

<header class="app-header">
	<div class="header-content">
		<div class="brand">
			<div class="brand-icon">
				<Layers size={24} />
			</div>
			<div class="brand-text">
				<h1 class="title">{title}</h1>
				{#if subtitle}
					<span class="subtitle">{subtitle}</span>
				{/if}
			</div>
		</div>
		{#if legend}
			<div class="legend-wrapper">
				{@render legend()}
			</div>
		{/if}
		<button
			type="button"
			class="theme-toggle"
			onclick={() => onThemeToggle?.()}
			title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
			aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
		>
			{#if theme === 'light'}
				<Moon size={16} />
			{:else}
				<Sun size={16} />
			{/if}
			<span class="sr-only">Toggle theme</span>
		</button>
	</div>
</header>

<style>
	.app-header {
		left: 0;
		right: 0;
		z-index: 1000;
		background: linear-gradient(180deg, var(--color-bg-overlay) 0%, var(--color-bg-elevated) 100%);
		backdrop-filter: blur(12px);
		border-bottom: 1px solid var(--color-border-subtle);
		flex-shrink: 0;
	}

	.header-content {
		height: 56px;
		padding: 0 20px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: 100%;
	}

	.brand {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.brand-icon {
		width: 28px;
		height: 28px;
		color: var(--color-accent-primary);
		opacity: 0.9;
	}

	.brand-text {
		display: flex;
		align-items: baseline;
		gap: 10px;
	}

	.title {
		font-size: 15px;
		font-weight: 600;
		color: var(--color-text-primary);
		letter-spacing: -0.2px;
		margin: 0;
	}

	.subtitle {
		font-size: 12px;
		color: var(--color-text-muted);
		font-weight: 500;
	}

	.legend-wrapper {
		margin-left: auto;
	}

	.theme-toggle {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		margin-left: 12px;
		background: var(--color-bg-glass);
		border: 1px solid var(--color-border-default);
		border-radius: 8px;
		color: var(--color-text-secondary);
		cursor: pointer;
		transition: background var(--duration-fast) ease, border-color var(--duration-fast) ease, color var(--duration-fast) ease;
	}

	.theme-toggle:hover {
		background: var(--color-bg-panel-hover);
		border-color: var(--color-border-hover);
		color: var(--color-accent-primary);
	}

	@media (max-width: 768px) {
		.header-content {
			height: auto;
			padding: 12px 16px;
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.legend-wrapper {
			margin-left: 40px;
		}

		.theme-toggle {
			margin-left: 0;
			align-self: flex-end;
		}
	}
</style>
